import type { Editor } from '@tiptap/core'
import { EditorContent, useEditorState } from '@tiptap/react'
import { Toolbar } from './MarkdownToolbar'
import { useRichTextContent } from './useRichTextContent'
import './MarkdownEditor.css'

interface MarkdownEditorProps {
	defaultValue?: string
	onChangeMarkdown?: (markdown: string) => void
	rows?: number
	maxWords?: number
	maxCharacters?: number
	placeholder?: string
	reloadOnContentChange?: boolean
}

export default function MarkdownEditor({
	defaultValue = '',
	onChangeMarkdown,
	placeholder,
	maxWords,
	maxCharacters,
	reloadOnContentChange = false,
}: MarkdownEditorProps) {
	const editor = useRichTextContent({
		content: defaultValue,
		onUpdate: onChangeMarkdown,
		reloadOnContentChange,
	})

	return (
		// biome-ignore lint/a11y/noStaticElementInteractions: editor container needs mouse event interception
		<div
			className='editor-container text-left w-full'
			onMouseDown={(e) => e.stopPropagation()}
			onMouseMove={(e) => e.stopPropagation()}>
			<Toolbar editor={editor} />

			<div style={{ position: 'relative' }}>
				<EditorContent editor={editor} placeholder={placeholder} contentEditable={false} />
				<CharacterLimit editor={editor} maxCharacters={maxCharacters} maxWords={maxWords} />
			</div>
		</div>
	)
}

function CharacterLimit({
	editor,
	maxCharacters,
	maxWords,
}: {
	editor: Editor
	maxCharacters?: number
	maxWords?: number
}) {
	const limitState = useEditorState({
		editor,
		selector: (context) => {
			const hasMaxWords = typeof maxWords === 'number'
			if (hasMaxWords) {
				const wordsCount = context.editor.storage.characterCount.words()

				return {
					counter: wordsCount,
					max: maxWords,
					isOverLimit: wordsCount > maxWords,
					limitType: 'words',
				}
			}

			const hasMaxCharacters = typeof maxCharacters === 'number'
			if (hasMaxCharacters) {
				const charactersCount = context.editor.storage.characterCount.characters()

				return {
					counter: charactersCount,
					max: maxCharacters,
					isOverLimit: charactersCount > maxCharacters,
					limitType: 'characters',
				}
			}

			return null
		},
	})

	if (!limitState) {
		return null
	}

	const { counter, max, isOverLimit, limitType } = limitState

	const percentage = editor ? Math.round((100 / max) * counter) : 0

	return (
		<div className={'character-count'}>
			<svg height='20' width='20' viewBox='0 0 20 20' aria-label='Character limit indicator'>
				<title>Character limit</title>
				<circle r='10' cx='10' cy='10' fill='#e9ecef' />
				<circle
					r='5'
					cx='10'
					cy='10'
					fill='transparent'
					stroke={isOverLimit ? 'red' : '#6a00f5'}
					strokeWidth='10'
					strokeDasharray={`calc(${percentage} * 31.4 / 100) 31.4`}
					transform='rotate(-90) translate(-20)'
				/>
				<circle r='6' cx='10' cy='10' fill='white' />
			</svg>
			{counter <= max ? (
				`${counter} / ${max} ${limitType}`
			) : (
				<>
					{counter - max} {limitType} over limit
					<br />
					You can save but not submit
				</>
			)}
		</div>
	)
}
